import { IAuthContext } from '../context/auth/auth-context';
import { Link, useLocation } from 'react-router-dom';
import React, { useEffect } from 'react';
import { TIERS } from '../types/tiers';
import styled from 'styled-components';
import withAuthContext from '../context/auth/AuthConsumer';
import { Nav } from 'react-bootstrap';
import EnviroChatIcon from '../assets/sidebar/enviro-chat.svg';
import SentimentIcon from '../assets/sidebar/sentiment.svg';
import ValidatorIcon from '../assets/sidebar/validator.svg';
import CorrectiveActionIcon from '../assets/sidebar/corrective-action.svg';
import GeneratorIcon from '../assets/sidebar/generator.svg';
import AdminPanelIcon from '../assets/sidebar/admin-panel.svg';
import ReactTooltip from 'react-tooltip';
import { InfoOutlined as InfoOutlinedIcon } from '@material-ui/icons';
import LayersOutlinedIcon from '@material-ui/icons/LayersOutlined';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import PublicOutlinedIcon from '@material-ui/icons/PublicOutlined';
import ShowChartOutlinedIcon from '@material-ui/icons/ShowChartOutlined';
import EqualizerOutlinedIcon from '@material-ui/icons/EqualizerOutlined';
import PersonOutlineOutlinedIcon from '@material-ui/icons/PersonOutlineOutlined';
import { GitBranch, Wind } from 'react-feather';
import { removeGlobalFlag } from '../store/aiearth/action';
import { useDispatch, useSelector } from 'react-redux';
import { projectId } from '../services/firebase';
import JurisdictionSelector from './JurisdictionSelector';

interface ISidebarProps {
  isCollapsed: boolean;
  onItemClick: () => void;
  onLogoutClick: () => void;
  onLoginClick: () => void;
  hideAuthButton?: boolean;
}
interface ISidebarContainerProps {
  isCollapsed: boolean;
}
interface ISidebarItemProps {
  isActive?: boolean;
}

const SidebarContainer = styled.div<ISidebarContainerProps>`
  display: flex;
  max-width: 252px;
  min-width: 232px;
  width: 100%;
  align-items: flex-start;
  position: fixed;
  height: 100vh;
  overflow: auto;
  background: rgba(7, 10, 13, 0.97);
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 12px 0 40px rgba(0, 0, 0, 0.35);
  top: 0;
  flex-direction: column;
  transition: all 0.3s;
  z-index: 800;
  scrollbar-width: thin;
  scrollbar-color: rgba(52, 211, 153, 0.35) rgba(15, 23, 42, 0.5);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.45);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(52, 211, 153, 0.35);
    border-radius: 4px;
    border: 2px solid rgba(15, 23, 42, 0.45);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(52, 211, 153, 0.55);
  }

  ${(prop) => (!!prop.isCollapsed ? 'margin-left: -320px' : '')}
`;

const ItemIconBox = styled.span`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.015);
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  img {
    width: 18px;
    height: 18px;
    opacity: 1;
    filter: brightness(0) saturate(100%) invert(92%) sepia(8%) saturate(427%) hue-rotate(174deg)
      brightness(95%) contrast(90%);
  }
`;

const ItemTextCol = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
`;

const ItemTitleRow = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
  justify-content: flex-start;
  min-width: 0;
`;

const ItemTitleText = styled.span<{ $active?: boolean }>`
  font-family: var(--sans, 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif);
  font-size: 14px;
  font-weight: 500;
  color: ${(p) => (p.$active ? '#f8fafc' : '#cbd5e1')};
  line-height: 1.25;
  letter-spacing: 0.01em;
  transition: color 0.15s ease;
  flex-shrink: 0;
  white-space: nowrap;
`;

const ItemSubtitle = styled.span`
  font-family: var(--sans, 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif);
  font-size: 12px;
  font-weight: 400;
  color: #546170;
  line-height: 1.35;
  letter-spacing: 0.02em;
`;

const ItemTitleMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const NewBadge = styled.span`
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #000;
  background: #3aefb8;
  padding: 3px 8px;
  border-radius: 8px;
  line-height: 1;
`;

const SidebarItem = styled(Link)<ISidebarItemProps>`
  text-decoration: none;
  cursor: pointer;
  position: relative;
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  color: inherit;
  padding: 10px 12px 10px 14px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  line-height: 1.25;
  width: calc(100% - 20px);
  border: 1px solid
    ${(props: ISidebarItemProps) =>
      !!props.isActive ? 'rgba(255, 255, 255, 0.08)' : 'transparent'};
  border-radius: 12px;
  margin: 0 10px 8px;
  background: ${(props: ISidebarItemProps) =>
    !!props.isActive
      ? 'linear-gradient(90deg, rgba(8, 56, 44, 0.82) 0%, rgba(10, 48, 39, 0.82) 100%)'
      : 'transparent'};
  box-shadow: ${(props: ISidebarItemProps) =>
    !!props.isActive ? 'inset 0 0 0 1px rgba(52, 211, 153, 0.1)' : 'none'};
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 14px;
    bottom: 14px;
    width: 3px;
    border-radius: 3px;
    background: ${(props: ISidebarItemProps) =>
      !!props.isActive ? 'rgba(52, 211, 153, 0.85)' : 'transparent'};
  }

  ${(props: ISidebarItemProps) =>
    !!props.isActive &&
    `
    ${ItemIconBox} {
      background: rgba(52, 211, 153, 0.08);
      border-color: rgba(52, 211, 153, 0.14);
    }
    .cmn-enviro-agent-icon {
      color: #34d399 !important;
    }
  `}

  &:hover {
    text-decoration: none;
    background: linear-gradient(90deg, rgba(10, 72, 56, 0.82) 0%, rgba(12, 60, 47, 0.82) 100%);
    border-color: rgba(52, 211, 153, 0.14);

    ${ItemIconBox} {
      border-color: rgba(52, 211, 153, 0.14);
      background: rgba(52, 211, 153, 0.08);
    }

    ${ItemTitleText} {
      color: #f8fafc;
    }
  }

  .MuiSvgIcon-root {
    font-size: 18px;
    color: #94a3b8;
    flex-shrink: 0;
    transition: color 0.15s ease;
  }

  &:hover .MuiSvgIcon-root {
    color: #94a3b8;
  }

  &:hover .cmn-enviro-agent-icon {
    color: #34d399 !important;
  }
`;

const EnviroAgentIcon = styled(LayersOutlinedIcon)`
  color: #34d399 !important;
`;

const AssistChatIcon = styled(ChatBubbleOutlineIcon)`
  color: #94a3b8 !important;
  font-size: 18px !important;
`;

const EnviroMultiModelIcon = styled(GitBranch)`
  color: #94a3b8 !important;
  width: 1rem !important;
`;

const DeepResearchIcon = styled(SearchOutlinedIcon)`
  color: #94a3b8 !important;
  font-size: 20px !important;
`;

const AiSearchIcon = styled(DescriptionOutlinedIcon)`
  color: #94a3b8 !important;
  font-size: 19px !important;
`;

const AiEarthSidebarIcon = styled(PublicOutlinedIcon)`
  color: #facc15 !important;
  font-size: 20px !important;
`;

const TrackBotSidebarIcon = styled(ShowChartOutlinedIcon)`
  color: #94a3b8 !important;
  font-size: 20px !important;
`;

const BenchmarkingSidebarIcon = styled(EqualizerOutlinedIcon)`
  color: #94a3b8 !important;
  font-size: 20px !important;
`;

const MyEnvironmentIcon = styled(PersonOutlineOutlinedIcon)`
  color: #94a3b8 !important;
  font-size: 18px !important;
`;

const AiAirIcon = styled(Wind)`
  color: #3b82f6;
  width: 18px;
  height: 18px;
  stroke-width: 2.25;
`;

const Aermod3DIcon = styled(Wind)`
  color: #a855f7;
  width: 18px;
  height: 18px;
  stroke-width: 2.25;
`;

const SidebarBrandHeader = styled(Link)`
  border: none;
  margin: 0;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 16px 14px;
  cursor: pointer;
  position: sticky;
  top: 0;
  background-color: rgba(7, 10, 13, 0.98);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  z-index: 1;
  text-align: left;
  text-decoration: none !important;
  color: inherit !important;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.04);
    text-decoration: none !important;
    color: inherit !important;
  }

  &:focus,
  &:active,
  &:visited {
    text-decoration: none !important;
    color: inherit !important;
  }

  * {
    text-decoration: none !important;
  }
`;

const BrandMark = styled.span`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #3aefb8;
  color: #000;
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.02em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-transform: lowercase;
`;

const BrandTitle = styled.span`
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  font-size: 1.05rem;
  font-weight: 600;
  color: #f8fafc;
  letter-spacing: -0.02em;
`;

const BrandDot = styled.span`
  color: #3aefb8;
`;
// const Title = styled.h2`
//   font-family: 'Lazy Dog';
//   font-size: 36px;
//   font-weight: 400;
//   letter-spacing: 1px;
//   margin: 26px 0 30px;
// `;
const SubTitle = styled.h3`
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: #546170;
  margin-bottom: 12px;
`;
const SubTitleWithoutLink = styled.h3`
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: #546170;
  margin-bottom: 15px;
  padding-left: 18px;
  padding-top: 15px;
`;
// const TundraPro = styled.a`
//   font-family: 'Sans Pro';
//   font-size: 26px;
//   padding-left: 15%;
//   text-transform: uppercase;
//   font-weight: 400;
//   letter-spacing: 1px;
//   color: inherit;
//   margin-bottom: 35px;
//   &:hover {
//     text-decoration: none;
//   }
// `;
const ExternalLink = styled.a`
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  color: #94a3b8;
  padding-left: 18px;
  text-decoration: none;
  transition: color 0.15s ease;
  &:hover {
    color: #ecf0f5;
    text-decoration: none;
  }
`;
const PageLink = styled(Link)`
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  color: #94a3b8;
  padding-left: 18px;
  text-decoration: none;
  transition: color 0.15s ease;
  &:hover {
    color: #ecf0f5;
    text-decoration: none;
  }
`;
const IconActionButton = styled(Nav.Link)`
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
  font-weight: 600;
  color: #94a3b8;
  text-decoration: none;
  padding: 0;
  transition: color 0.15s ease;
  &:hover {
    color: #ecf0f5;
    text-decoration: none;
  }
`;

const StyledTooltip = styled(ReactTooltip)`
  max-width: 25% !important;
`;

const Sidebar: React.FC<ISidebarProps & { context: IAuthContext }> = (
  props: ISidebarProps & { context: IAuthContext }
) => {
  const authContext = props.context;
  const isAuthenticated = !!(props.context.user && props.context.user.uid);
  const location = useLocation();
  const store: any = useSelector<any>((state): any => state);
  const dispatch = useDispatch();
  const useQuery = () => {
    const { search: searchVal } = location;

    return React.useMemo(() => new URLSearchParams(searchVal), [searchVal]);
  };
  const queryParam = useQuery();
  const normalizePath = (value: string) => {
    const normalized = value.replace(/\/+$/, '');
    return normalized || '/';
  };
  const isActivePath = (path: string) => {
    const currentPath = normalizePath(location.pathname);
    const targetPath = normalizePath(path);
    return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
  };

  const aermod3DUrl = process.env.REACT_APP_AERMOD_3D_URL;

  // Remove lastQuestion flag from localStorage on refresh page and browser back button
  useEffect(() => {
    // window.addEventListener('load', () => localStorage.removeItem('lastQuestion'));
    // performance.navigation.type detects page is refresh or not
    if (
      performance.navigation &&
      performance.navigation.type &&
      performance.navigation.type === 1 &&
      (!store.globalFlags.lastQuestion || !localStorage.getItem('lastQuestion'))
    ) {
      localStorage.removeItem('lastQuestion');
    }
    // return () => {
    //   window.removeEventListener('beforeunload', () => localStorage.removeItem('lastQuestion'));
    // };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remove previousPage flag on route change
  useEffect(() => {
    // reset temp chat in local storage
    localStorage.removeItem('tempChatData');
    if (
      (location.pathname !== '/search' && location.pathname !== '/my-tundra') ||
      (location.pathname === '/search' && !!!location.search)
    ) {
      dispatch(removeGlobalFlag('previousPage'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, queryParam.get('type')]);

  const handleLogout = (e: any) => {
    e.preventDefault();
    props.context.signOut().then(async () => {
      try {
        await fetch(
          process.env.NODE_ENV === 'development'
            ? `${process.env.REACT_APP_CERES_URL}/api/logout`
            : `${window.location.origin}/logout`,
          { method: 'POST', credentials: 'include' }
        );
      } catch (_) {
        // do nothing
      }
      props.onLogoutClick();
    });
  };

  return (
    <SidebarContainer isCollapsed={props.isCollapsed}>
      <StyledTooltip place="right" effect="solid" />
      <SidebarBrandHeader
        to="/enviro-chat"
        onClick={() => props.onItemClick()}
        aria-label="Go to homepage"
      >
        <BrandMark aria-hidden>ai</BrandMark>
        <BrandTitle>
          enviro
          <BrandDot>.</BrandDot>
          ai
        </BrandTitle>
      </SidebarBrandHeader>
      <JurisdictionSelector />
      {/* <Title>BY ENVIRO.AI </Title> */}
      {/* <TundraPro href="https://www.enviro.ai/pricing" onClick={() => props.onItemClick()} >
        Tundra pro
      </TundraPro> */}
      <SubTitleWithoutLink>INTELLIGENCE</SubTitleWithoutLink>
      {(authContext.isLubrizol() ||
        authContext.isTier(TIERS.PLATINUM) ||
        authContext.isTier(TIERS.DAYPASS) ||
        authContext.isTier(TIERS.SUPER_ADMIN)) && (
        <SidebarItem
          to="/enviro-chat"
          isActive={isActivePath('/enviro-chat')}
          onClick={() => props.onItemClick()}
        >
          <ItemIconBox>
            <AssistChatIcon />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/enviro-chat')}>Enviro Chat</ItemTitleText>
              <ItemTitleMeta>
                <NewBadge>NEW</NewBadge>
              </ItemTitleMeta>
            </ItemTitleRow>
            <ItemSubtitle>Smart query routing</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )}
      {(authContext.isLubrizol() ||
        authContext.isTier(TIERS.PLATINUM) ||
        authContext.isTier(TIERS.DAYPASS) ||
        authContext.isTier(TIERS.SUPER_ADMIN)) && (
        <SidebarItem
          to="/myenviroai"
          isActive={isActivePath('/myenviroai')}
          onClick={() => props.onItemClick()}
        >
          <ItemIconBox>
            <EnviroMultiModelIcon />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/myenviroai')}>EnviroMulti-Model</ItemTitleText>
            </ItemTitleRow>
            <ItemSubtitle>Ask anything</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )}
      {(authContext.isLubrizol() ||
        authContext.isTier(TIERS.PLATINUM) ||
        authContext.isTier(TIERS.DAYPASS) ||
        authContext.isTier(TIERS.SUPER_ADMIN)) && (
        <SidebarItem
          to="/enviro-deep-research"
          isActive={isActivePath('/enviro-deep-research')}
          onClick={() => props.onItemClick()}
        >
          <ItemIconBox>
            <DeepResearchIcon />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/enviro-deep-research')}>
                Enviro Deep Research
              </ItemTitleText>
            </ItemTitleRow>
            <ItemSubtitle>Multi-source analysis</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )}
      {(authContext.isLubrizol() ||
        authContext.isTier(TIERS.PLATINUM) ||
        authContext.isTier(TIERS.DAYPASS) ||
        authContext.isTier(TIERS.SUPER_ADMIN)) && (
        <SidebarItem
          to="/search"
          isActive={isActivePath('/search')}
          onClick={() => props.onItemClick()}
        >
          <ItemIconBox>
            <AiSearchIcon />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/search')}>Enviro AI Search</ItemTitleText>
            </ItemTitleRow>
            <ItemSubtitle>11M+ documents</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )}
      <SubTitleWithoutLink>AGENT</SubTitleWithoutLink>
      {(authContext.isLubrizol() ||
        authContext.isTier(TIERS.PLATINUM) ||
        authContext.isTier(TIERS.DAYPASS) ||
        authContext.isTier(TIERS.SUPER_ADMIN)) && (
        <SidebarItem
          to="/permit-agent"
          isActive={isActivePath('/permit-agent')}
          onClick={() => props.onItemClick()}
        >
          <ItemIconBox>
            <EnviroAgentIcon className="cmn-enviro-agent-icon" />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/permit-agent')}>Permit Agent</ItemTitleText>
            </ItemTitleRow>
            <ItemSubtitle>Permits &amp; compliance</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )}
      <SubTitleWithoutLink>GEOSPATIAL</SubTitleWithoutLink>
      {(authContext.isLubrizol() ||
        authContext.isTier(TIERS.PLATINUM) ||
        authContext.isTier(TIERS.DAYPASS) ||
        authContext.isTier(TIERS.SUPER_ADMIN)) && (
        <SidebarItem
          to="/my-tundra"
          isActive={isActivePath('/my-tundra')}
          onClick={() => props.onItemClick()}
        >
          <ItemIconBox>
            <MyEnvironmentIcon />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/my-tundra')}>My Facilities</ItemTitleText>
            </ItemTitleRow>
            <ItemSubtitle>Sites &amp; facilities</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )}
      {(authContext.isLubrizol() ||
        authContext.isTier(TIERS.PLATINUM) ||
        authContext.isTier(TIERS.DAYPASS) ||
        authContext.isTier(TIERS.SUPER_ADMIN)) && (
        <SidebarItem
          to="/ai-earth"
          isActive={isActivePath('/ai-earth')}
          onClick={() => props.onItemClick()}
        >
          <ItemIconBox>
            <AiEarthSidebarIcon />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/ai-earth')}>AI Earth</ItemTitleText>
              <ItemTitleMeta>
                <InfoOutlinedIcon data-tip="Interactive mapping of environmental data and compliance information" />
              </ItemTitleMeta>
            </ItemTitleRow>
            <ItemSubtitle>Geospatial &amp; maps</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )}
      {(authContext.isLubrizol() ||
        authContext.isTier(TIERS.PLATINUM) ||
        authContext.isTier(TIERS.DAYPASS) ||
        authContext.isTier(TIERS.SUPER_ADMIN)) &&
        process.env.REACT_APP_ENVIRONMENT !== 'production' && (
          <SidebarItem
            to="/ai-earth-air"
            isActive={isActivePath('/ai-earth-air')}
            onClick={() => props.onItemClick()}
            title="Air intelligence"
          >
            <ItemIconBox>
              <AiAirIcon />
            </ItemIconBox>
            <ItemTextCol>
              <ItemTitleRow>
                <ItemTitleText $active={isActivePath('/ai-earth-air')}>Ai Air</ItemTitleText>
                <ItemTitleMeta>
                  <NewBadge>NEW</NewBadge>
                </ItemTitleMeta>
              </ItemTitleRow>
              <ItemSubtitle>AERMOD &amp; DAP</ItemSubtitle>
            </ItemTextCol>
          </SidebarItem>
        )}
      <SubTitleWithoutLink>TOOLS</SubTitleWithoutLink>
      {/* {(authContext.isLubrizol() ||
        authContext.isTier(TIERS.PLATINUM) ||
        authContext.isTier(TIERS.DAYPASS) ||
        authContext.isTier(TIERS.SUPER_ADMIN)) && (
        <SidebarItem
          to="/phase1-agent"
          isActive={isActivePath('/phase1-agent')}
          onClick={() => props.onItemClick()}
        >
          <ItemIconBox>
            <EnviroAgentIcon className="cmn-enviro-agent-icon" />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/phase1-agent')}>Phase1 Tool</ItemTitleText>
              <ItemTitleMeta>
                <NewBadge>BETA</NewBadge>
              </ItemTitleMeta>
            </ItemTitleRow>
            <ItemSubtitle>Continuous property due diligence</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )} */}
      {!!aermod3DUrl &&
        (authContext.isLubrizol() ||
          authContext.isTier(TIERS.PLATINUM) ||
          authContext.isTier(TIERS.DAYPASS) ||
          authContext.isTier(TIERS.SUPER_ADMIN)) && (
          <SidebarItem
            to="/aermod3d"
            isActive={isActivePath('/aermod3d')}
            onClick={() => props.onItemClick()}
          >
            <ItemIconBox>
              <Aermod3DIcon />
            </ItemIconBox>
            <ItemTextCol>
              <ItemTitleRow>
                <ItemTitleText $active={isActivePath('/aermod3d')}>Enviro Aermod3D</ItemTitleText>
                <ItemTitleMeta>
                  <NewBadge>BETA</NewBadge>
                </ItemTitleMeta>
              </ItemTitleRow>
              <ItemSubtitle>Industrial Spatial Intelligence</ItemSubtitle>
            </ItemTextCol>
          </SidebarItem>
        )}
      {(authContext.isLubrizol() ||
        authContext.isTier(TIERS.PLATINUM) ||
        authContext.isTier(TIERS.DAYPASS) ||
        authContext.isTier(TIERS.SUPER_ADMIN)) && (
        <SidebarItem
          to="/trackbot"
          isActive={isActivePath('/trackbot')}
          onClick={() => props.onItemClick()}
        >
          <ItemIconBox>
            <TrackBotSidebarIcon />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/trackbot')}>TrackBot</ItemTitleText>
              <ItemTitleMeta>
                <InfoOutlinedIcon data-tip="Track agency filings and other environmental changes" />
              </ItemTitleMeta>
            </ItemTitleRow>
            <ItemSubtitle>Agency submissions</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )}
      {/* {(authContext.isTier(TIERS.PLATINUM) || authContext.isTier(TIERS.DAYPASS) || authContext.isTier(TIERS.SUPER_ADMIN)) && (
        <SidebarItem to="/people-finder" isActive={isActivePath('/people-finder')} onClick={() => props.onItemClick()}>
          <ItemIconBox>
            <img src={PeopleFinderIcon} alt="" aria-hidden />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/people-finder')}>People Finder</ItemTitleText>
            </ItemTitleRow>
            <ItemSubtitle>Contacts &amp; profiles</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )} */}
      {(authContext.isLubrizol() ||
        authContext.isTier(TIERS.PLATINUM) ||
        authContext.isTier(TIERS.DAYPASS) ||
        authContext.isTier(TIERS.SUPER_ADMIN)) && (
        <SidebarItem
          to="/sentiment"
          isActive={isActivePath('/sentiment')}
          onClick={() => props.onItemClick()}
          title="Environmental sentiment analysis"
        >
          <ItemIconBox>
            <img src={SentimentIcon} alt="" aria-hidden />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/sentiment')}>Sentiment</ItemTitleText>
            </ItemTitleRow>
            <ItemSubtitle>News &amp; perception signals</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )}
      {(authContext.isLubrizol() ||
        authContext.isTier(TIERS.PLATINUM) ||
        authContext.isTier(TIERS.DAYPASS) ||
        authContext.isTier(TIERS.SUPER_ADMIN)) && (
        <SidebarItem
          to="/validator"
          isActive={isActivePath('/validator')}
          onClick={() => props.onItemClick()}
        >
          <ItemIconBox>
            <img src={ValidatorIcon} alt="" aria-hidden />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/validator')}>Validator</ItemTitleText>
              <ItemTitleMeta>
                <InfoOutlinedIcon data-tip="Reviews Phase 1 environmental reports" />
              </ItemTitleMeta>
            </ItemTitleRow>
            <ItemSubtitle>Phase 1 report reviews</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )}

      {(authContext.isLubrizol() ||
        authContext.isTier(TIERS.PLATINUM) ||
        authContext.isTier(TIERS.DAYPASS) ||
        authContext.isTier(TIERS.SUPER_ADMIN)) && (
        <SidebarItem
          to="/benchmarking"
          isActive={isActivePath('/benchmarking')}
          onClick={() => props.onItemClick()}
        >
          <ItemIconBox>
            <BenchmarkingSidebarIcon />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/benchmarking')}>Benchmarking</ItemTitleText>
              <ItemTitleMeta>
                <InfoOutlinedIcon data-tip="Benchmarks company environmental performance" />
              </ItemTitleMeta>
            </ItemTitleRow>
            <ItemSubtitle>Emissions</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )}
      {(authContext.isLubrizol() ||
        authContext.isTier(TIERS.PLATINUM) ||
        authContext.isTier(TIERS.DAYPASS) ||
        authContext.isTier(TIERS.SUPER_ADMIN)) && (
        <SidebarItem
          to="/corrective-tracker"
          isActive={isActivePath('/corrective-tracker')}
          onClick={() => props.onItemClick()}
        >
          <ItemIconBox>
            <img src={CorrectiveActionIcon} alt="" aria-hidden />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/corrective-tracker')}>
                Corrective Action
              </ItemTitleText>
            </ItemTitleRow>
            <ItemSubtitle>Compliance tracking</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )}
      {(authContext.isLubrizol() ||
        authContext.isTier(TIERS.PLATINUM) ||
        authContext.isTier(TIERS.DAYPASS) ||
        authContext.isTier(TIERS.SUPER_ADMIN)) && (
        <SidebarItem
          to="/generator"
          isActive={isActivePath('/generator')}
          onClick={() => props.onItemClick()}
        >
          <ItemIconBox>
            <img src={GeneratorIcon} alt="" aria-hidden />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/generator')}>Generator</ItemTitleText>
              <ItemTitleMeta>
                <InfoOutlinedIcon data-tip="Identifies potential emission reduction opportunities" />
              </ItemTitleMeta>
            </ItemTitleRow>
            <ItemSubtitle>Reduction opportunities</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )}
      {projectId === 'ceres-platform-test' &&
        (authContext.isLubrizol() ||
          authContext.isTier(TIERS.PLATINUM) ||
          authContext.isTier(TIERS.DAYPASS) ||
          authContext.isTier(TIERS.SUPER_ADMIN)) && (
          <SidebarItem
            to="/enviro-chemical-ai"
            isActive={isActivePath('/enviro-chemical-ai')}
            onClick={() => props.onItemClick()}
          >
            <ItemIconBox>
              <img src={EnviroChatIcon} alt="" aria-hidden />
            </ItemIconBox>
            <ItemTextCol>
              <ItemTitleRow>
                <ItemTitleText $active={isActivePath('/enviro-chemical-ai')}>
                  Enviro Chemical AI
                </ItemTitleText>
              </ItemTitleRow>
              <ItemSubtitle>Chemical intelligence tools</ItemSubtitle>
            </ItemTextCol>
          </SidebarItem>
        )}
      {authContext.isTier(TIERS.SUPER_ADMIN) && (
        <SidebarItem
          to="/admin/users"
          isActive={isActivePath('/admin/users')}
          onClick={() => props.onItemClick()}
        >
          <ItemIconBox>
            <img src={AdminPanelIcon} alt="" aria-hidden />
          </ItemIconBox>
          <ItemTextCol>
            <ItemTitleRow>
              <ItemTitleText $active={isActivePath('/admin/users')}>Admin Panel</ItemTitleText>
            </ItemTitleRow>
            <ItemSubtitle>System administration</ItemSubtitle>
          </ItemTextCol>
        </SidebarItem>
      )}
      <br />
      <PageLink to="/account" onClick={() => props.onItemClick()}>
        <SubTitle>Account</SubTitle>
      </PageLink>

      {/* <ExternalLink
        href="https://info.enviro.ai/carbon-sequestration"
        target="_blank"
        rel="noreferrer"
        onClick={() => props.onItemClick()}
      >
        <SubTitle>How it works</SubTitle>
      </ExternalLink> */}
      <ExternalLink
        href="https://info.enviro.ai/privacy-policy"
        target="_blank"
        rel="noreferrer"
        onClick={() => props.onItemClick()}
      >
        <SubTitle>Privacy policy</SubTitle>
      </ExternalLink>
      <ExternalLink
        href="https://info.enviro.ai/terms-and-conditions"
        target="_blank"
        rel="noreferrer"
        onClick={() => props.onItemClick()}
      >
        <SubTitle>Terms & Conditions</SubTitle>
      </ExternalLink>
      <ExternalLink
        href="https://info.enviro.ai/contact"
        target="_blank"
        rel="noreferrer"
        onClick={() => props.onItemClick()}
      >
        <SubTitle>Contact</SubTitle>
      </ExternalLink>
      <ExternalLink
        href="https://info.enviro.ai"
        target="_blank"
        rel="noreferrer"
        onClick={() => props.onItemClick()}
      >
        <SubTitle>About</SubTitle>
      </ExternalLink>

      {isAuthenticated ? (
        <SubTitleWithoutLink>
          <IconActionButton title="Logout" href="#" onClick={handleLogout} rel="noreferrer">
            Log Out
          </IconActionButton>
        </SubTitleWithoutLink>
      ) : (
        <SubTitleWithoutLink>
          <IconActionButton
            title="Login"
            href="#"
            onClick={() => props.onLoginClick()}
            rel="noreferrer"
          >
            Log In
          </IconActionButton>
        </SubTitleWithoutLink>
      )}
    </SidebarContainer>
  );
};

export default withAuthContext(Sidebar);
